import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../api/client";
import { GroupCharacter } from "../pages/GroupPage";
import { useCampaign } from "./context";
import { CampaignError, useAction } from "./parts";
import type { PartyMember } from "./types";

/**
 * The party with its game values like the group page, from the group cards of the modules. A member without a character, or one
 * whose character this user may not read, shows as a card with its name (F1).
 */
export function PartyPage() {
  return (
    <section className="stack">
      <PartyCards />
    </section>
  );
}

/** The party as cards with game values, on its page and in the situation. */
export function PartyCards() {
  const campaign = useCampaign();
  const party = campaign.head.party;
  return (
    <>
      <h2>{campaign.term("party")}</h2>
      {party.length === 0 && (
        <p className="muted">
          Noch niemand eingetragen.{campaign.canWrite && <> Neue kommen in den <Link to={`/k/${campaign.id}/einstellungen`}>Einstellungen</Link> dazu.</>}
        </p>
      )}
      <ul className="group-list" aria-label={campaign.term("party")}>
        {party.map((member) => (
          <li key={member.id} className="stack">
            {member.characterId ? (
              <GroupCharacter id={member.characterId} name={member.name} path={`/c/${member.characterId}`} fallback={<NameCard member={member} />} />
            ) : (
              <NameCard member={member} />
            )}
            {member.characterId && member.mine && !campaign.leads && <ShareToggle member={member} characterId={member.characterId} />}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The owner decides whether the game masters may read the character (F17); linking it to the party alone does not let them.
 * Players may give this consent: it concerns their character, not the campaign, and no undo of the campaign takes it back.
 */
function ShareToggle({ member, characterId }: { member: PartyMember; characterId: string }) {
  const campaign = useCampaign();
  const client = useQueryClient();
  const action = useAction();
  const toggle = () =>
    action.run(async () => {
      const head = await campaign.request((headers) =>
        api.PUT("/api/campaigns/{id}/shares/{characterId}", { params: { path: { id: campaign.id, characterId } }, body: { shared: !member.shared }, headers }),
      );
      client.setQueryData(campaign.key("head"), head);
    });
  return (
    <div className="row">
      <span className="muted small grow">
        {member.shared ? "Freigegeben" : "Nicht freigegeben"} – {campaign.term("gameMaster")}:{" "}
        {member.shared
          ? "liest deinen Charakter und darf ihm Belohnungen beim Abschluss gutschreiben (etwa Erfahrung oder Geld). Sonst ändert die Freigabe nichts."
          : "sieht deinen Charakter nicht. Die Freigabe erlaubt das Lesen und Gutschriften von Belohnungen beim Abschluss (etwa Erfahrung oder Geld), sonst nichts."}
      </span>
      <button type="button" className="button secondary" disabled={action.busy} onClick={() => void toggle()}>
        {member.shared ? "Freigabe zurücknehmen" : "Für die Kampagne freigeben"}
      </button>
      <CampaignError error={action.error} onClose={action.clear} />
    </div>
  );
}

function NameCard({ member }: { member: PartyMember }) {
  const campaign = useCampaign();
  const player = member.playerName ?? campaign.head.members.find((m) => m.userId === member.playerUserId)?.name;
  return (
    <article className="card group-card" aria-label={member.name}>
      <header className="group-card-header">
        <h2>{member.entryId ? <Link to={campaign.entryPath(member.entryId)}>{member.name}</Link> : member.name}</h2>
        {player && <p className="muted small">{player}</p>}
      </header>
    </article>
  );
}
